import apiClient from '../utils/apiClient';

/**
 * Credit Note Service
 * Handles all API calls related to credit notes
 */
const creditNoteService = {
  /**
   * Get all credit notes with optional filtering
   * @param {Object} filters - Filter parameters
   * @param {Number} page - Page number for pagination
   * @param {Number} limit - Number of items per page
   * @returns {Promise} - Promise resolving to credit notes data
   */
  getCreditNotes: async (filters = {}, page = 1, limit = 10) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      // Add pagination
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      
      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await apiClient.get(`/creditNotes?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching credit notes:', error);
      throw error.response?.data || { message: 'Error fetching credit notes' };
    }
  },
  
  /**
   * Get a credit note by invoice number
   * @param {String} invoiceNo - Credit note invoice number
   * @returns {Promise} - Promise resolving to credit note data
   */
  getCreditNoteByInvoiceNo: async (invoiceNo) => {
    try {
      const response = await apiClient.get(`/creditNotes/${invoiceNo}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching credit note ${invoiceNo}:`, error);
      throw error.response?.data || { message: 'Error fetching credit note' };
    }
  },
  
  /**
   * Get the next available credit note number
   * @returns {Promise} - Promise resolving to next credit note number
   */
  getNextCreditNoteNumber: async () => {
    try {
      const response = await apiClient.get('/creditNotes/next-invoice-number');
      return response.data;
    } catch (error) {
      console.error('Error fetching next credit note number:', error);
      throw error.response?.data || { message: 'Error fetching next credit note number' };
    }
  },
  
  /**
   * Create a new credit note
   * @param {Object} creditNoteData - Credit note data
   * @returns {Promise} - Promise resolving to created credit note
   */
  createCreditNote: async (creditNoteData) => {
    try {
      const response = await apiClient.post('/creditNotes', creditNoteData);
      return response.data;
    } catch (error) {
      console.error('Error creating credit note:', error);
      throw error.response?.data || { message: 'Error creating credit note' };
    }
  },
  
  /**
   * Update an existing credit note
   * @param {String} invoiceNo - Credit note invoice number
   * @param {Object} creditNoteData - Updated credit note data
   * @returns {Promise} - Promise resolving to updated credit note
   */
  updateCreditNote: async (invoiceNo, creditNoteData) => {
    try {
      const response = await apiClient.put(`/creditNotes/${invoiceNo}`, creditNoteData);
      return response.data;
    } catch (error) {
      console.error(`Error updating credit note ${invoiceNo}:`, error);
      throw error.response?.data || { message: 'Error updating credit note' };
    }
  },
  
  /**
   * Delete a credit note
   * @param {String} invoiceNo - Credit note invoice number
   * @returns {Promise} - Promise resolving to deletion result
   */
  deleteCreditNote: async (invoiceNo) => {
    try {
      const response = await apiClient.delete(`/creditNotes/${invoiceNo}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting credit note ${invoiceNo}:`, error);
      throw error.response?.data || { message: 'Error deleting credit note' };
    }
  }
};

export default creditNoteService;
